import mapboxgl from 'mapbox-gl';

// Mapbox configuration
mapboxgl.accessToken = 'pk.eyJ1IjoiZ2FpYW5ldGVjb3N5c3RlbW1hcCIsImEiOiJjbWVyMHdudngwMWxkMmlxeXQwdjEycXQxIn0.4m6ycLsmBBu4GQ7ZGTtjtA';

// Data URL - direct S3 access (CloudFront is configured for website bucket, not data bucket)
const DATA_URL = (window as any).__DATA_URL__ || 'https://gaianet-globe-data.s3.amazonaws.com/gaianet-globe-data.json';

// Create the map
const map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/mapbox/satellite-streets-v12',
    projection: 'globe',
    center: [0, 30],
    zoom: 2,
    antialias: true
});

// Add navigation controls
map.addControl(new mapboxgl.NavigationControl());

// Add globe atmosphere
map.on('style.load', () => {
    map.setFog({
        'color': 'rgb(186, 210, 235)',
        'high-color': 'rgb(36, 92, 223)',
        'horizon-blend': 0.02,
        'space-color': 'rgb(11, 11, 25)',
        'star-intensity': 0.6
    });
});

// Load and display data
map.on('load', async () => {
    try {
        console.log('Fetching data from:', DATA_URL);
        
        const response = await fetch(DATA_URL);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const geoJSON = await response.json();
        console.log('Data loaded:', geoJSON.properties);
        
        // Helper to check if feature is virtual
        const isVirtualFeature = (f: any) =>
            f?.properties?.locationType === 'special' ||
            f?.properties?.isSpecial ||
            f?.properties?.location === 'Global' ||
            f?.properties?.location === 'Remote';
        
        // Function to set up map layers (can be called after style changes)
        const setupMapLayers = (data: any) => {
            // Remove existing source if it exists
            if (map.getSource('gaianet-data')) {
                try {
                    if (map.getLayer('projects-layer')) map.removeLayer('projects-layer');
                    if (map.getLayer('members-layer')) map.removeLayer('members-layer');
                    if (map.getLayer('spaces-layer')) map.removeLayer('spaces-layer');
                    map.removeSource('gaianet-data');
                } catch (e) {
                    // Source/layers might not exist yet
                }
            }
            
            // Add source
            map.addSource('gaianet-data', {
                type: 'geojson',
                data: data
            });
            
            // Add layer for projects (green dots)
            map.addLayer({
                id: 'projects-layer',
                type: 'circle',
                source: 'gaianet-data',
                filter: ['==', ['get', 'type'], 'Projects'],
                paint: {
                    'circle-radius': 6,
                    'circle-color': '#4CAF50',
                    'circle-stroke-color': '#fff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.95
                }
            });

            // Add layer for members (blue dots)
            map.addLayer({
                id: 'members-layer',
                type: 'circle',
                source: 'gaianet-data',
                filter: ['==', ['get', 'type'], 'Members'],
                paint: {
                    'circle-radius': 5,
                    'circle-color': '#2196F3',
                    'circle-stroke-color': '#fff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.95
                }
            });

            // Add layer for spaces (orange dots)
            map.addLayer({
                id: 'spaces-layer',
                type: 'circle',
                source: 'gaianet-data',
                filter: ['==', ['get', 'type'], 'Spaces'],
                paint: {
                    'circle-radius': 6,
                    'circle-color': '#FF9800',
                    'circle-stroke-color': '#fff',
                    'circle-stroke-width': 2,
                    'circle-opacity': 0.95
                }
            });
        };
        
        // Initial layer setup - filter out virtual projects by default
        const initialPhysicalData = {
            type: 'FeatureCollection',
            features: geoJSON.features.filter((f: any) => !isVirtualFeature(f))
        };
        setupMapLayers(initialPhysicalData);
        
        // Popup handler
        const showPopup = (e: mapboxgl.MapMouseEvent) => {
            if (!e.features || !e.features[0] || !e.features[0].properties) return;
            
            const props = e.features[0].properties!;
            const coords = (e.features[0].geometry as any).coordinates.slice();
            const nodeType = props.type;
            const accentColor = nodeType === 'Projects' ? '#4CAF50' : nodeType === 'Spaces' ? '#FF9800' : '#2196F3';
            
            // Build basic info (always shown)
            const parseMultiValue = (value: any): string[] | null => {
                if (Array.isArray(value)) {
                    return value.map(item => String(item)).filter(Boolean);
                }
                if (typeof value === 'string') {
                    const trimmed = value.trim();
                    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
                        try {
                            const parsed = JSON.parse(trimmed);
                            if (Array.isArray(parsed)) {
                                return parsed.map(item => String(item)).filter(Boolean);
                            }
                        } catch {
                            return null;
                        }
                    }
                }
                return null;
            };

            const renderChips = (items: string[]): string =>
                `<span class="popup-chips">${items.map(item => `<span class="popup-chip">${item}</span>`).join('')}</span>`;

            const renderValue = (value: any): string => {
                const parsed = parseMultiValue(value);
                if (parsed && parsed.length > 0) {
                    return renderChips(parsed);
                }
                return String(value);
            };

            const sectorValue = props.sector || props.sectors;
            const sectorDisplay = sectorValue ? renderValue(sectorValue) : '';
            const descriptionValue = props.description || props.bio;
            const basicInfo = `
                <h3 style="margin: 0 0 8px 0; font-size: 16px;">${props.name}</h3>
                ${props.logoUrl ? `<img src="${props.logoUrl}" style="max-width: 100%; max-width: 110px; margin: 4px 0; border-radius: 4px;" />` : ''}
                ${props.pictureUrl ? `<img src="${props.pictureUrl}" style="max-width: 100%; max-width: 110px; margin: 4px 0; border-radius: 4px;" />` : ''}
                ${props.imageUrl ? `<img src="${props.imageUrl}" style="max-width: 100%; max-width: 110px; margin: 4px 0; border-radius: 4px;" />` : ''}
                ${descriptionValue ? `<p style="margin: 4px 0; font-size: 12px; line-height: 1.3;"><strong>${props.type === 'Members' ? 'Bio' : 'Project Description'}:</strong> ${descriptionValue}</p>` : ''}
                <p style="margin: 4px 0; font-size: 12px; line-height: 1.3;"><strong>Location:</strong> ${props.location || 'Unknown'}</p>
                ${sectorValue ? `<p style="margin: 4px 0; font-size: 12px; line-height: 1.3;"><strong>Sector:</strong> ${sectorDisplay}</p>` : ''}
            `;
            
            // Build extended info (hidden by default)
            const extendedFields: string[] = [];
            
            // Add all available fields
            Object.keys(props).forEach(key => {
                const value = props[key];
                // Skip fields already shown or empty/null
                if (['id', 'name', 'type', 'location', 'sector', 'sectors', 'description', 'bio', 'logoUrl', 'pictureUrl', 'imageUrl', 'locationType', 'locationConfidence', 'locationRaw', 'locationSource', 'originalLocation', 'isSpecial', 'displayType'].includes(key)) {
                    return;
                }
                if (value && value !== 'Unknown' && value !== '' && !Array.isArray(value) || (Array.isArray(value) && value.length > 0)) {
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
                    let displayValue = '';
                    if (Array.isArray(value)) {
                        displayValue = renderChips(value.map(item => String(item)).filter(Boolean));
                    } else if (typeof value === 'object') {
                        displayValue = JSON.stringify(value);
                    } else {
                        displayValue = renderValue(value);
                    }
                    if (displayValue.length > 200) {
                        displayValue = displayValue.substring(0, 200) + '...';
                    }
                    extendedFields.push(`<p style="margin: 4px 0; font-size: 12px; line-height: 1.3;"><strong>${label}:</strong> ${displayValue}</p>`);
                }
            });
            
            const extendedInfo = extendedFields.length > 0 ? `
                <div id="extended-info" style="display: none; margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd;">
                    ${extendedFields.join('')}
                </div>
            ` : '';
            
            const showMoreButton = extendedFields.length > 0 ? `
                <button id="show-more-btn" style="margin-top: 8px; padding: 5px 10px; background: ${accentColor}; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 11px;">
                    Show More Details
                </button>
            ` : '';
            
            const popupHTML = `
                <div style="max-width: 220px;">
                    ${basicInfo}
                    ${extendedInfo}
                    ${showMoreButton}
                    ${props.website ? `<p style="margin: 8px 0 0 0; font-size: 12px;"><a href="${props.website}" target="_blank" style="color: ${accentColor}; text-decoration: none; font-weight: 500;">Visit Website →</a></p>` : ''}
                </div>
            `;

            const mapSize = map.getContainer().getBoundingClientRect();
            const point = map.project(coords);
            const baseAnchor = coords[1] >= 0 ? 'top' : 'bottom';
            const marginX = 180;
            const marginY = 120;

            let vertAnchor = baseAnchor;
            if (baseAnchor === 'top' && point.y < marginY) {
                vertAnchor = 'bottom';
            }
            if (baseAnchor === 'bottom' && point.y > mapSize.height - marginY) {
                vertAnchor = 'top';
            }

            let horizAnchor = '';
            if (point.x < marginX) {
                horizAnchor = 'left';
            } else if (point.x > mapSize.width - marginX) {
                horizAnchor = 'right';
            }

            const anchor = (horizAnchor ? `${vertAnchor}-${horizAnchor}` : vertAnchor) as mapboxgl.Anchor;
            const offset: Partial<Record<mapboxgl.Anchor, [number, number]>> = {
                'top': [0, 10],
                'top-left': [10, 10],
                'top-right': [-10, 10],
                'bottom': [0, -10],
                'bottom-left': [10, -10],
                'bottom-right': [-10, -10],
                'left': [10, 0],
                'right': [-10, 0]
            };

            new mapboxgl.Popup({
                maxWidth: '240px',
                anchor,
                offset,
                closeButton: false,
                closeOnClick: true,
                closeOnMove: true,
                focusAfterOpen: false,
                className: 'globe-popup'
            })
                .setLngLat(coords)
                .setHTML(popupHTML)
                .addTo(map);
            
            // Add click handler for "Show More" button
            if (extendedFields.length > 0) {
                setTimeout(() => {
                    const btn = document.getElementById('show-more-btn');
                    const extended = document.getElementById('extended-info');
                    if (btn && extended) {
                        btn.addEventListener('click', () => {
                            if (extended.style.display === 'none') {
                                extended.style.display = 'block';
                                btn.textContent = 'Show Less';
                            } else {
                                extended.style.display = 'none';
                                btn.textContent = 'Show More Details';
                            }
                        });
                    }
                }, 100);
            }
        };
        
        // Click handlers
        map.on('click', 'projects-layer', showPopup);
        map.on('click', 'members-layer', showPopup);
        map.on('click', 'spaces-layer', showPopup);
        
        // Hover cursor
        map.on('mouseenter', 'projects-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
    });
        map.on('mouseleave', 'projects-layer', () => {
        map.getCanvas().style.cursor = '';
    });
    map.on('mouseenter', 'members-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', 'members-layer', () => {
        map.getCanvas().style.cursor = '';
    });
        map.on('mouseenter', 'spaces-layer', () => {
        map.getCanvas().style.cursor = 'pointer';
    });
        map.on('mouseleave', 'spaces-layer', () => {
        map.getCanvas().style.cursor = '';
    });
        
        // Setup filters (pass setupMapLayers function for style switching)
        setupFilters(geoJSON, setupMapLayers);
        
        // Initialize counter (will be updated by setupFilters)
        document.getElementById('totalCount')!.textContent = geoJSON.features.length.toString();
        
        console.log('✅ Globe loaded successfully!');
        console.log(`   Projects: ${geoJSON.properties.projectsCount}`);
        console.log(`   Members: ${geoJSON.properties.membersCount}`);
        console.log(`   Spaces: ${geoJSON.properties.spacesCount}`);
        
    } catch (error: any) {
        console.error('❌ Failed to load data:', error);
        alert(`Error loading globe: ${error?.message || 'Unknown error'}`);
    }
});

// Setup filter functionality
function setupFilters(geoJSON: any, setupMapLayersFn: (data: any) => void) {
    // Virtual world style management
    let isVirtualWorldMode = false;
    const virtualWorldStyle = 'mapbox://styles/mapbox/dark-v11';
    const physicalWorldStyle = 'mapbox://styles/mapbox/satellite-streets-v12';
    
    // Helper function to check if feature is virtual (defined in setupFilters scope)
    const isVirtual = (f: any) =>
        f?.properties?.locationType === 'special' ||
        f?.properties?.isSpecial ||
        f?.properties?.location === 'Global' ||
        f?.properties?.location === 'Remote';
    
    // Function to switch to virtual world style
    const switchToVirtualWorld = () => {
        if (isVirtualWorldMode) return;
        
        console.log('🌐 Switching to virtual world...');
        isVirtualWorldMode = true;
        
        map.setStyle(virtualWorldStyle);
        
        map.once('style.load', () => {
            // Hide all label layers
            const style = map.getStyle();
            if (style && style.layers) {
                style.layers.forEach((layer: any) => {
                    try {
                        if (layer.id.includes('label') || layer.id.includes('Label') ||
                            layer.id.includes('text') || layer.id.includes('Text')) {
                            if (map.getLayer(layer.id)) {
                                map.setLayoutProperty(layer.id, 'visibility', 'none');
                            }
                        }
                        // Hide boundaries, roads, but keep water/landcover for texture
                        if (layer.id.includes('boundary') || layer.id.includes('admin') ||
                            layer.id.includes('road') || layer.id.includes('waterway') ||
                            layer.id.includes('building') || layer.id.includes('bridge') ||
                            layer.id.includes('tunnel') || layer.id.includes('ferry') ||
                            layer.id.includes('rail') || layer.id.includes('poi') ||
                            layer.id.includes('settlement') || layer.id.includes('airport')) {
                            if (map.getLayer(layer.id)) {
                                map.setLayoutProperty(layer.id, 'visibility', 'none');
                            }
                        }
                    } catch (e) {}
                });
            }
            
            // Set purple/reddish background
            try {
                if (map.getLayer('background')) {
                    map.setPaintProperty('background', 'background-color', '#1a0a1a');
                } else {
                    map.addLayer({
                        id: 'custom-background',
                        type: 'background',
                        paint: { 'background-color': '#1a0a1a' }
                    }, 'background');
                }
            } catch (e) {}
            
            // Purple/reddish fog
            map.setFog({
                'color': 'rgb(26, 10, 26)',
                'high-color': 'rgb(120, 30, 80)',
                'horizon-blend': 0.4,
                'space-color': 'rgb(10, 5, 15)',
                'star-intensity': 0.9
            });
            
            setupMapLayersFn(geoJSON);
            setTimeout(() => applyFilters(), 100);
        });
    };
    
    // Function to switch back to physical world style
    const switchToPhysicalWorld = () => {
        if (!isVirtualWorldMode) return;
        
        console.log('🌍 Switching to physical world...');
        isVirtualWorldMode = false;
        
        map.setStyle(physicalWorldStyle);
        
        map.once('style.load', () => {
            map.setFog({
                'color': 'rgb(186, 210, 235)',
                'high-color': 'rgb(36, 92, 223)',
                'horizon-blend': 0.02,
                'space-color': 'rgb(11, 11, 25)',
                'star-intensity': 0.6
            });
            
            setupMapLayersFn(geoJSON);
            setTimeout(() => applyFilters(), 100);
        });
    };
    const filtersPanel = document.querySelector('.filters') as HTMLElement | null;
    const toggleButton = document.getElementById('toggleFilters');
    if (filtersPanel && toggleButton) {
        // Ensure filters start collapsed
        filtersPanel.classList.add('collapsed');
        
        toggleButton.addEventListener('click', () => {
            filtersPanel.classList.toggle('collapsed');
        });
    }

    const sectors = [
        'Arts Culture & Music',
        'Food Environment Water & Wildlife',
        'Health & Wellness',
        'Housing Infrastructure & Transport',
        'Indigenous & Ancient Wisdom',
        'Justice Sovereignty & Governance',
        'Learning & Education',
        'Love & Sexuality',
        'Media & Communication',
        'Relations Peacebuilding & Community',
        'Resource Sharing Economics & Trade',
        'Science Technology & Energy',
        'Spirituality Personal Development & Consciousness'
    ];
    
    // Populate sector dropdown
    const sectorFilter = document.getElementById('sectorFilter') as HTMLSelectElement;
    sectors.forEach(sector => {
        const option = document.createElement('option');
        option.value = sector;
        option.textContent = sector;
        sectorFilter.appendChild(option);
    });

    // Map of cities/abbreviations to their countries
    const cityToCountry: { [key: string]: string } = {
        // Abbreviations
        'USA': 'United States',
        'US': 'United States',
        'UK': 'United Kingdom',
        'UAE': 'United Arab Emirates',
        
        // European cities
        'Amsterdam': 'Netherlands',
        'The Hague': 'Netherlands',
        'Utrecht': 'Netherlands',
        'Winsum': 'Netherlands',
        'Berlin': 'Germany',
        'Munich': 'Germany',
        'Hamburg': 'Germany',
        'Frankfurt': 'Germany',
        'London': 'United Kingdom',
        'Manchester': 'United Kingdom',
        'Edinburgh': 'United Kingdom',
        'Glasgow': 'United Kingdom',
        'Exeter': 'United Kingdom',
        'Paris': 'France',
        'Barcelona': 'Spain',
        'Madrid': 'Spain',
        'Rome': 'Italy',
        'Milan': 'Italy',
        'Vienna': 'Austria',
        'Brussels': 'Belgium',
        'Lisbon': 'Portugal',
        'Dublin': 'Ireland',
        'Copenhagen': 'Denmark',
        'Stockholm': 'Sweden',
        'Oslo': 'Norway',
        'Helsinki': 'Finland',
        'Prague': 'Czech Republic',
        'Warsaw': 'Poland',
        'Budapest': 'Hungary',
        'Zurich': 'Switzerland',
        'Geneva': 'Switzerland',
        'Athens': 'Greece',
        'Belgrade': 'Serbia',
        'Bucharest': 'Romania',
        'Sofia': 'Bulgaria',
        'Moscow': 'Russia',
        
        // North American cities
        'New York': 'United States',
        'San Francisco': 'United States',
        'Los Angeles': 'United States',
        'Chicago': 'United States',
        'Boston': 'United States',
        'Seattle': 'United States',
        'Austin': 'United States',
        'Portland': 'United States',
        'Denver': 'United States',
        'Miami': 'United States',
        'Atlanta': 'United States',
        'Washington': 'United States',
        'Berkeley': 'United States',
        'Boulder': 'United States',
        'Santa Fe': 'United States',
        'Richmond': 'United States',
        'Folsom': 'United States',
        'Grass Valley': 'United States',
        'Bay Area': 'United States',
        'Toronto': 'Canada',
        'Montreal': 'Canada',
        'Vancouver': 'Canada',
        'Calgary': 'Canada',
        'Ottawa': 'Canada',
        'Qualicum Beach': 'Canada',
        'Mexico City': 'Mexico',
        'Tulum': 'Mexico',
        'Matamoros': 'Mexico',
        
        // Central/South American cities
        'Rio de Janeiro': 'Brazil',
        'Sao Paulo': 'Brazil',
        'Teresopolis': 'Brazil',
        'Buenos Aires': 'Argentina',
        'Santiago': 'Chile',
        'Bogota': 'Colombia',
        'Lima': 'Peru',
        'Nosara': 'Costa Rica',
        'Ometepe': 'Nicaragua',
        'Lake Atitlan': 'Guatemala',
        
        // Asian cities
        'Tokyo': 'Japan',
        'Beijing': 'China',
        'Shanghai': 'China',
        'Hong Kong': 'China',
        'Mumbai': 'India',
        'Delhi': 'India',
        'Bengaluru': 'India',
        'Hyderabad': 'India',
        'Kharghar': 'India',
        'Bangkok': 'Thailand',
        'Seoul': 'South Korea',
        'Singapore': 'Singapore',
        'Taipei': 'Taiwan',
        'Manila': 'Philippines',
        'Jakarta': 'Indonesia',
        'Bali': 'Indonesia',
        'Kuala Lumpur': 'Malaysia',
        
        // Other cities
        'Sydney': 'Australia',
        'Melbourne': 'Australia',
        'Tel Aviv': 'Israel',
        'Jerusalem': 'Israel',
        'Dubai': 'United Arab Emirates',
        'Istanbul': 'Turkey',
        'Cairo': 'Egypt',
        'Nairobi': 'Kenya',
        'Cape Town': 'South Africa',
        'Johannesburg': 'South Africa'
    };
    
    // Get unique countries from data (map cities to countries)
    const countries = new Set<string>();
    geoJSON.features.forEach((f: any) => {
        const location = f.properties.location;
        if (!location || location === 'Unknown' || location === 'Global') return;
        
        // Map city to country, or use as-is if already a country
        const country = cityToCountry[location] || location;
        countries.add(country);
    });
    
    // Populate country dropdown
    const countryFilter = document.getElementById('countryFilter') as HTMLSelectElement;
    Array.from(countries).sort().forEach(country => {
        const option = document.createElement('option');
        option.value = country;
        option.textContent = country;
        countryFilter.appendChild(option);
    });
    
    
    // Virtual grid sidebar removed per user request
    const virtualGrid = document.getElementById('virtualGrid') as HTMLElement | null;
    if (virtualGrid) {
        virtualGrid.style.display = 'none';
    }

    // Apply filters function (isVirtual is already defined above in setupFilters scope)
    const applyFilters = () => {
        console.log('🔍 Applying filters...');
        
        const typeFilter = (document.getElementById('typeFilter') as HTMLSelectElement).value;
        const sectorFilter = (document.getElementById('sectorFilter') as HTMLSelectElement).value;
        const countryFilter = (document.getElementById('countryFilter') as HTMLSelectElement).value;
        const showGlobal = (document.getElementById('showGlobal') as HTMLInputElement).checked;
        const searchInput = (document.getElementById('search') as HTMLInputElement).value.toLowerCase().trim();
        
        // Handle virtual world mode switching
        if (showGlobal && !isVirtualWorldMode) {
            switchToVirtualWorld();
        } else if (!showGlobal && isVirtualWorldMode) {
            switchToPhysicalWorld();
        }
        
        // Filter data client-side
        const filteredData: any[] = [];
        const filteredVirtual: any[] = [];

        geoJSON.features.forEach((f: any) => {
            const virtual = isVirtual(f);
            
            // In virtual world mode, only show virtual projects
            // In physical world mode, hide virtual projects unless showGlobal is checked
            if (isVirtualWorldMode) {
                // Virtual world: only show virtual projects
                if (!virtual) {
                    return;
                }
            } else {
                // Physical world: hide virtual projects unless showGlobal is checked
                if (!showGlobal && virtual) {
                    return;
                }
                // When showGlobal is checked in physical mode, show both
            }
            
            // Type filter
            if (typeFilter !== 'all' && f.properties.type !== typeFilter) {
                return;
            }
            
            // Sector filter
            if (sectorFilter !== 'all') {
                const sectorValue = f.properties.sector || f.properties.sectors || '';
                const featureSectors = String(sectorValue).toLowerCase();
                if (!featureSectors.includes(sectorFilter.toLowerCase())) {
                    return;
                }
            }
            
            // Country filter
            if (countryFilter !== 'all') {
                if (virtual) {
                    return;
                }
                const location = f.properties.location;
                // Use the same cityToCountry mapping
                const country = cityToCountry[location] || location;
                if (country !== countryFilter) {
                    return;
                }
            }
            
            // Search filter
            if (searchInput.length > 0) {
                const searchableText = [
                    f.properties.name,
                    f.properties.description,
                    f.properties.bio,
                    f.properties.sector,
                    f.properties.sectors,
                    f.properties.skills,
                    f.properties.skillsInHouse,
                    f.properties.tags,
                    f.properties.networks,
                    f.properties.passions,
                    f.properties.superpowers,
                    f.properties.offers,
                    f.properties.needs,
                    f.properties.spaces,
                    f.properties.assets
                ].filter(Boolean).join(' ').toLowerCase();
                
                if (!searchableText.includes(searchInput)) {
                    return;
                }
            }
            
            // In virtual world mode, add virtual features to filteredData (they'll be shown on globe)
            // In physical world mode, separate them for the sidebar display
            if (virtual) {
                if (isVirtualWorldMode) {
                    // Virtual world: add virtual features to main data so they appear on globe
                    filteredData.push(f);
                } else {
                    // Physical world: separate virtual features for sidebar only
                    filteredVirtual.push(f);
                }
                return;
            }
            filteredData.push(f);
        });
        
        // Update source with filtered data (includes virtual features in virtual mode)
        const source = map.getSource('gaianet-data') as mapboxgl.GeoJSONSource;
        if (source) {
            source.setData({
                type: 'FeatureCollection',
                features: filteredData
            });
        }

        // Hide virtual grid sidebar (removed per user request)
        if (virtualGrid) {
            virtualGrid.classList.add('hidden');
        }
        
        // Update counter
        const visibleCount = filteredData.length;
        document.getElementById('visibleCount')!.textContent = visibleCount.toString();
        
        console.log(`Filtered to ${visibleCount} features (${filteredData.filter((f: any) => f.properties.type === 'Projects').length} projects, ${filteredData.filter((f: any) => f.properties.type === 'Members').length} members, ${filteredData.filter((f: any) => f.properties.type === 'Spaces').length} spaces, ${filteredVirtual.length} virtual)`);
    };
    
    // Attach filter event listeners
    document.getElementById('typeFilter')!.addEventListener('change', applyFilters);
    document.getElementById('sectorFilter')!.addEventListener('change', applyFilters);
    document.getElementById('countryFilter')!.addEventListener('change', applyFilters);
    document.getElementById('showGlobal')!.addEventListener('change', applyFilters);
    document.getElementById('search')!.addEventListener('input', applyFilters);
    
    // Expose applyFilters for initial call
    (window as any).__applyFilters = applyFilters;
    
    // Apply initial filter to show only physical projects
    applyFilters();
}

